import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { http, apiError, billingHooks } from "@/lib/api";
import { db, cacheAll, localRows, enqueue, queueCount, ENTITIES } from "@/lib/db";
import { trackEvent } from "@/lib/analytics";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function resolveLogo(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) {
    const token = localStorage.getItem("dm_token") || "";
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
  }
  return url;
}

const hexToRgba = (hex, alpha) => {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return `rgba(37,99,235,${alpha})`;
  const n = parseInt(clean, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [clinic, setClinic] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [data, setData] = useState({ patients: [], visits: [], consultants: [], settlements: [] });
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState(localStorage.getItem("dm_view") || "doctor");

  const isOwner = user?.role === "owner";
  const isSuperAdmin = !!user?.is_super_admin;
  const showMoney = isOwner && viewMode === "doctor";

  const subscriptionRef = useRef(null);
  const applySubscription = useCallback((sub) => {
    subscriptionRef.current = sub || null;
    setSubscription(sub || null);
  }, []);

  const refreshPending = useCallback(async () => setPending(await queueCount()), []);

  const loadLocal = useCallback(async () => {
    const out = {};
    for (const e of ENTITIES) out[e] = await localRows(e);
    setData(out);
  }, []);

  const refreshSubscription = useCallback(async () => {
    const { data: res } = await http.get("/billing");
    applySubscription(res);
    return res;
  }, [applySubscription]);

  const pullAll = useCallback(async () => {
    if (subscriptionRef.current && subscriptionRef.current.has_access === false) {
      await loadLocal();
      return;
    }
    setSyncing(true);
    try {
      const items = await db.queue.toArray();
      const ops = items.map((i) => ({ entity: i.entity, op: i.op, data: i.data }));
      const { data: res } = await http.post("/sync", { ops });
      if (items.length) await db.queue.bulkDelete(items.map((i) => i.qid));
      for (const e of ENTITIES) {
        // Archived consultants stay cached so past visits and reports keep their names.
        const rows = (res.changes[e] || []).filter((r) => !r.deleted || e === "consultants");
        await cacheAll(e, rows);
      }
      await loadLocal();
    } catch (e) {
      console.warn("sync failed", apiError(e));
    } finally {
      await refreshPending();
      setSyncing(false);
    }
  }, [loadLocal, refreshPending]);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("dm_token");
    if (!token) return setUser(false);
    try {
      const { data: res } = await http.get("/auth/me");
      setUser(res.user);
      setClinic(res.clinic);
      applySubscription(res.subscription);
      if (res.user.role !== "owner") setViewMode("assistant");
      if (res.clinic) {
        if (res.subscription?.has_access === false) await loadLocal();
        else await pullAll();
      }
    } catch (e) {
      await loadLocal();
      if (!navigator.onLine) setUser({ offline: true, role: "owner", name: "Offline" });
      else {
        localStorage.removeItem("dm_token");
        setUser(false);
      }
    }
  }, [pullAll, loadLocal, applySubscription]);

  useEffect(() => {
    billingHooks.onLocked = () => refreshSubscription().catch(() => {});
    return () => {
      billingHooks.onLocked = null;
    };
  }, [refreshSubscription]);

  useEffect(() => {
    bootstrap();
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (online && user && clinic) pullAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  useEffect(() => {
    const root = document.documentElement;
    const accent = clinic?.primary_color || "#2563EB";
    const accent2 = clinic?.secondary_color || "#0EA5A4";
    root.style.setProperty("--dm-accent", accent);
    root.style.setProperty("--dm-accent-2", accent2);
    root.style.setProperty("--dm-accent-soft", hexToRgba(accent, 0.12));
    root.style.setProperty("--dm-accent-ring", hexToRgba(accent, 0.2));
  }, [clinic]);

  const login = async (email, password) => {
    const { data: res } = await http.post("/auth/login", { email, password });
    localStorage.setItem("dm_token", res.token);
    setUser(res.user);
    if (res.user.role !== "owner") setViewMode("assistant");
    const me = await http.get("/auth/me");
    setClinic(me.data.clinic);
    applySubscription(me.data.subscription);
    if (me.data.clinic) {
      if (me.data.subscription?.has_access === false) await loadLocal();
      else await pullAll();
    }
    trackEvent("login", { method: "password", role: res.user.role });
    return res.user;
  };

  const register = async (payload) => {
    const { data: res } = await http.post("/auth/register", payload);
    localStorage.setItem("dm_token", res.token);
    setUser(res.user);
    if (res.user.role !== "owner") setViewMode("assistant");
    const me = await http.get("/auth/me");
    setClinic(me.data.clinic);
    applySubscription(me.data.subscription);
    if (me.data.clinic) {
      if (me.data.subscription?.has_access === false) await loadLocal();
      else await pullAll();
    }
    trackEvent("sign_up", { method: "password", role: res.user.role });
    return res.user;
  };

  // After the mobile number is confirmed the server returns the updated user.
  const applyUser = (u) => setUser(u);

  // Google sign-in / sign-up. The API checks Google's signed token; we only ever send it that credential.
  const googleAuth = async (payload) => {
    const { data: res } = await http.post("/auth/google", payload);
    localStorage.setItem("dm_token", res.token);
    setUser(res.user);
    if (res.user.role !== "owner") setViewMode("assistant");
    const me = await http.get("/auth/me");
    setClinic(me.data.clinic);
    applySubscription(me.data.subscription);
    if (me.data.clinic) {
      if (me.data.subscription?.has_access === false) await loadLocal();
      else await pullAll();
    }
    trackEvent(payload.mode === "signup" ? "sign_up" : "login", { method: "google", role: res.user.role });
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem("dm_token");
    setUser(false);
    setClinic(null);
  };

  const saveClinic = async (payload) => {
    const { data: res } = await http.post("/clinic", payload);
    setClinic(res);
    try {
      await refreshSubscription();
    } catch (e) {
      console.warn("refreshSubscription failed", apiError(e));
    }
    await pullAll();
    return res;
  };

  const save = async (entity, record) => {
    const doc = { ...record, updated_at: new Date().toISOString() };
    await enqueue(entity, doc);
    await loadLocal();
    await refreshPending();
    if (navigator.onLine) await pullAll();
    return doc;
  };

  // Several records saved as one step (patient → bill → visit → payment) with a single sync.
  const saveMany = async (items) => {
    const stamp = new Date().toISOString();
    const docs = [];
    for (const [entity, record] of items) {
      const doc = { ...record, updated_at: stamp };
      await enqueue(entity, doc);
      docs.push(doc);
    }
    await loadLocal();
    await refreshPending();
    if (navigator.onLine) await pullAll();
    return docs;
  };

  const remove = async (entity, id) => {
    await http.delete(`/${entity}/${id}`);
    await pullAll();
  };

  const restoreConsultant = async (id) => {
    await http.post(`/consultants/${id}/restore`);
    await pullAll();
  };

  const activeConsultants = useMemo(() => data.consultants.filter((c) => !c.deleted), [data.consultants]);

  const setMode = (m) => {
    setViewMode(m);
    localStorage.setItem("dm_view", m);
  };

  const hasAccess = subscription ? subscription.has_access : true;

  const value = useMemo(
    () => ({ user, clinic, subscription, hasAccess, data, activeConsultants, online, pending, syncing, viewMode, isOwner, isSuperAdmin, showMoney, accent: clinic?.primary_color || "#2563EB",
      accent2: clinic?.secondary_color || "#0EA5A4", logoSrc: resolveLogo(clinic?.logo_url),
      login, register, googleAuth, applyUser, logout, saveClinic, save, saveMany, remove, restoreConsultant, setMode, pullAll, setClinic, refreshSubscription }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, clinic, subscription, hasAccess, data, online, pending, syncing, viewMode]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
